import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, storage } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const PartnerForm = ({ user }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Application Type
    newApplication: false,
    renewalApplication: false,
    
    // Section 1: Entity Information
    entityRegisteredName: '',
    tradingName: '',
    businessRegistrationNumber: '',
    taxIdentificationNumber: '',
    directorNames: '',
    industrySector: '',
    officeContactNumber: '',
    businessEmail: '',
    physicalBusinessLocation: '',
    streetAddress: '',
    district: '',
    numberOfEmployees: '',
    website: '',
    socialMediaLinks: '',
    
    // Section 2: Business Liaison Contact
    liaisonTitleFullNames: '',
    liaisonJobPosition: '',
    liaisonDepartment: '',
    liaisonContactNumber: '',
    liaisonDirectEmail: '',
    
    // Section 3: Business Line of Operation Survey
    groceriesHouseholdEssentials: false,
    healthcareWellness: false,
    clothingPersonalCare: false,
    businessDevelopmentEntrepreneurship: false,
    entertainmentLiveEvents: false,
    housingUtilities: false,
    sportsRecreation: false,
    educationSkillsDevelopment: false,
    tourismArtsCulture: false,
    technologyConnectivity: false,
    travelMobility: false,
    lodgingAccommodation: false,
    industryFacilityTours: false,
    othersOperation: false,
    
    // Section 4: Business Engagement Areas
    generalManagement: false,
    operations: false,
    finance: false,
    purchasing: false,
    marketing: false,
    informationTechnology: false,
    humanResources: false,
    customerService: false,
    otherEngagement: '',
    
    // Section 5: Value-Added Offerings
    productServicePromotions: false,
    outdoorAdventures: false,
    industryFacilityToursValue: false,
    businessDevelopmentEntrepreneurshipValue: false,
    entertainmentLiveEventsValue: false,
    communityEngagementCSR: false,
    sportsRecreationValue: false,
    familyRelationshipsCounselling: false,
    tourismArtsCultureValue: false,
    wellnessHealthRelaxation: false,
    travelAccommodation: false,
    wealthFinancialPlanning: false,
    otherValueAdded: '',
    
    // Section 6: Entity Profile & Engagement
    yearsOfOperation: '',
    targetMarket: '',
    ageDemographics: [],
    effectiveMarketingChannels: [],
    collaborationFrequency: '',
    coHostSponsorMentor: '',
    specifyHow: [],
    
    // Section 7: Supplier Empowerment & Business Support
    clientCustomerSourcing: false,
    marketOpportunitiesSupport: false,
    betterPricesDealSuppliers: false,
    accessPreferredVendors: false,
    strategicSourcingSupport: false,
    representationSupplierNegotiations: false,
    flexibleSupplierPaymentTerms: false,
    jointSourcingEfforts: false,
    businessDevelopmentServices: false,
    enterpriseSupportServices: false,
    otherSupplierEmpowerment: '',
    
    // Section 8: Administration Fee & Payment Details
    agreeAdminFee: false,
    paymentMethod: '',
    mPesaNumber: '',
    ecoCashNumber: '',
    accountHolder: '',
    bankName: '',
    accountNo: '',
    branchCode: '',
    
    // Section 9: Agreement & Signature
    authorizedSignature: '',
    date: '',
    
    // Attachments
    personalIdChecked: false,
    personalIdFile: null,
    businessIdChecked: false,
    businessIdFile: null,
    businessTradersLicenseChecked: false,
    businessTradersLicenseFile: null,
    taxClearanceCertificateChecked: false,
    taxClearanceCertificateFile: null,
    businessUtilityBillChecked: false,
    businessUtilityBillFile: null,
    businessSubleaseAgreementChecked: false,
    businessSubleaseAgreementFile: null,
    
    // Section 10: Declaration
    applicantTitleFullNames: '',
    applicantPosition: '',
    applicantBusinessName: '',
    applicantDate: '',
    representativeTitleFullNames: '',
    representativePosition: 'FMLC Business Partner Coordinator',
    representativeDate: '',
    representativeSignature: '',
    approverTitleFullNames: '',
    approverPosition: 'FMLC Coordinator',
    approverDate: '',
    approverSignature: '',
    
    // Other Comments
    otherComments: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    // Load saved form data if exists
    const loadFormData = async () => {
      try {
        const docRef = doc(db, 'partnerApplications', user.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setFormData(prev => ({
            ...prev,
            ...docSnap.data(),
            ageDemographics: docSnap.data().ageDemographics || [],
            effectiveMarketingChannels: docSnap.data().effectiveMarketingChannels || [],
            specifyHow: docSnap.data().specifyHow || []
          }));
        }
      } catch (error) {
        console.error('Error loading form data:', error);
      } finally {
        setIsDataLoaded(true);
      }
    };
    
    loadFormData();
  }, [user.uid]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: e.target.files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCheckboxArray = (e) => {
    const { name, value, checked } = e.target;
    if (checked) {
      setFormData(prev => ({
        ...prev,
        [name]: [...(prev[name] || []), value]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: (prev[name] || []).filter(item => item !== value)
      }));
    }
  };

  const saveFormData = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'partnerApplications', user.uid), formData);
    } catch (error) {
      console.error('Error saving form data:', error);
    }
    setSaving(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Upload files
      let personalIdUrl = '';
      if (formData.personalIdFile) {
        const personalIdRef = ref(storage, `partner-docs/${user.uid}/personalId`);
        await uploadBytes(personalIdRef, formData.personalIdFile);
        personalIdUrl = await getDownloadURL(personalIdRef);
      }
      
      let businessIdUrl = '';
      if (formData.businessIdFile) {
        const businessIdRef = ref(storage, `partner-docs/${user.uid}/businessId`);
        await uploadBytes(businessIdRef, formData.businessIdFile);
        businessIdUrl = await getDownloadURL(businessIdRef);
      }
      
      let businessTradersLicenseUrl = '';
      if (formData.businessTradersLicenseFile) {
        const businessTradersLicenseRef = ref(storage, `partner-docs/${user.uid}/businessTradersLicense`);
        await uploadBytes(businessTradersLicenseRef, formData.businessTradersLicenseFile);
        businessTradersLicenseUrl = await getDownloadURL(businessTradersLicenseRef);
      }
      
      let taxClearanceCertificateUrl = '';
      if (formData.taxClearanceCertificateFile) {
        const taxClearanceCertificateRef = ref(storage, `partner-docs/${user.uid}/taxClearanceCertificate`);
        await uploadBytes(taxClearanceCertificateRef, formData.taxClearanceCertificateFile);
        taxClearanceCertificateUrl = await getDownloadURL(taxClearanceCertificateRef);
      }
      
      let businessUtilityBillUrl = '';
      if (formData.businessUtilityBillFile) {
        const businessUtilityBillRef = ref(storage, `partner-docs/${user.uid}/businessUtilityBill`);
        await uploadBytes(businessUtilityBillRef, formData.businessUtilityBillFile);
        businessUtilityBillUrl = await getDownloadURL(businessUtilityBillRef);
      }
      
      let businessSubleaseAgreementUrl = '';
      if (formData.businessSubleaseAgreementFile) {
        const businessSubleaseAgreementRef = ref(storage, `partner-docs/${user.uid}/businessSubleaseAgreement`);
        await uploadBytes(businessSubleaseAgreementRef, formData.businessSubleaseAgreementFile);
        businessSubleaseAgreementUrl = await getDownloadURL(businessSubleaseAgreementRef);
      }
      
      // Save form data with file URLs
      const formDataWithUrls = {
        ...formData,
        personalIdUrl,
        businessIdUrl,
        businessTradersLicenseUrl,
        taxClearanceCertificateUrl,
        businessUtilityBillUrl,
        businessSubleaseAgreementUrl,
        status: 'submitted',
        submittedAt: new Date()
      };
      
      await setDoc(doc(db, 'partnerApplications', user.uid), formDataWithUrls);
      
      alert('Application submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your application. Please try again.');
    }
    
    setLoading(false);
  };

  const nextStep = () => {
    saveFormData();
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const renderStep = () => {
    if (!isDataLoaded) {
      return <div>Loading...</div>;
    }

    switch(currentStep) {
      case 1:
        return (
          <div className="form-section">
            <h3>Application Type</h3>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="newApplication"
                  checked={formData.newApplication}
                  onChange={handleChange}
                />
                NEW APPLICATION
              </label>
              <label>
                <input
                  type="checkbox"
                  name="renewalApplication"
                  checked={formData.renewalApplication}
                  onChange={handleChange}
                />
                RENEWAL APPLICATION
              </label>
            </div>
            
            <h3>Section 1: Entity Information</h3>
            <div className="form-group">
              <label>1. Entity Registered Name</label>
              <input
                type="text"
                name="entityRegisteredName"
                value={formData.entityRegisteredName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>2. Trading Name</label>
              <input
                type="text"
                name="tradingName"
                value={formData.tradingName}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>3. Business Registration Number</label>
              <input
                type="text"
                name="businessRegistrationNumber"
                value={formData.businessRegistrationNumber}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>4. Tax Identification Number (TIN)</label>
              <input
                type="text"
                name="taxIdentificationNumber"
                value={formData.taxIdentificationNumber}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>5. Director Names</label>
              <input
                type="text"
                name="directorNames"
                value={formData.directorNames}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>6. Industry/Sector</label>
              <input
                type="text"
                name="industrySector"
                value={formData.industrySector}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>7. Office Contact Number</label>
              <input
                type="tel"
                name="officeContactNumber"
                value={formData.officeContactNumber}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>8. Business Email</label>
              <input
                type="email"
                name="businessEmail"
                value={formData.businessEmail}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>9. Physical Business Location</label>
              <input
                type="text"
                name="physicalBusinessLocation"
                value={formData.physicalBusinessLocation}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>10. Street Address</label>
              <input
                type="text"
                name="streetAddress"
                value={formData.streetAddress}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>11. District</label>
              <input
                type="text"
                name="district"
                value={formData.district}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>12. Number of Employees</label>
              <input
                type="number"
                name="numberOfEmployees"
                value={formData.numberOfEmployees}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>13. Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>14. Social Media Links</label>
              <textarea
                name="socialMediaLinks"
                value={formData.socialMediaLinks}
                onChange={handleChange}
                rows="3"
              />
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="form-section">
            <h3>Section 2: Business Liaison Contact</h3>
            <div className="form-group">
              <label>1. Title & Full Names</label>
              <input
                type="text"
                name="liaisonTitleFullNames"
                value={formData.liaisonTitleFullNames}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>2. Job Position</label>
              <input
                type="text"
                name="liaisonJobPosition"
                value={formData.liaisonJobPosition}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>3. Department</label>
              <input
                type="text"
                name="liaisonDepartment"
                value={formData.liaisonDepartment}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>4. Contact Number</label>
              <input
                type="tel"
                name="liaisonContactNumber"
                value={formData.liaisonContactNumber}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>5. Direct Email</label>
              <input
                type="email"
                name="liaisonDirectEmail"
                value={formData.liaisonDirectEmail}
                onChange={handleChange}
              />
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="form-section">
            <h3>Section 3: Business Line of Operation Survey</h3>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="groceriesHouseholdEssentials"
                  checked={formData.groceriesHouseholdEssentials}
                  onChange={handleChange}
                />
                Groceries & Household Essentials
              </label>
              <label>
                <input
                  type="checkbox"
                  name="healthcareWellness"
                  checked={formData.healthcareWellness}
                  onChange={handleChange}
                />
                Healthcare & Wellness
              </label>
              <label>
                <input
                  type="checkbox"
                  name="clothingPersonalCare"
                  checked={formData.clothingPersonalCare}
                  onChange={handleChange}
                />
                Clothing & Personal Care
              </label>
              <label>
                <input
                  type="checkbox"
                  name="businessDevelopmentEntrepreneurship"
                  checked={formData.businessDevelopmentEntrepreneurship}
                  onChange={handleChange}
                />
                Business Development & Entrepreneurship
              </label>
              <label>
                <input
                  type="checkbox"
                  name="entertainmentLiveEvents"
                  checked={formData.entertainmentLiveEvents}
                  onChange={handleChange}
                />
                Entertainment & Live Events
              </label>
              <label>
                <input
                  type="checkbox"
                  name="housingUtilities"
                  checked={formData.housingUtilities}
                  onChange={handleChange}
                />
                Housing & Utilities
              </label>
              <label>
                <input
                  type="checkbox"
                  name="sportsRecreation"
                  checked={formData.sportsRecreation}
                  onChange={handleChange}
                />
                Sports & Recreation
              </label>
              <label>
                <input
                  type="checkbox"
                  name="educationSkillsDevelopment"
                  checked={formData.educationSkillsDevelopment}
                  onChange={handleChange}
                />
                Education & Skills Development
              </label>
              <label>
                <input
                  type="checkbox"
                  name="tourismArtsCulture"
                  checked={formData.tourismArtsCulture}
                  onChange={handleChange}
                />
                Tourism, Arts & Culture
              </label>
              <label>
                <input
                  type="checkbox"
                  name="technologyConnectivity"
                  checked={formData.technologyConnectivity}
                  onChange={handleChange}
                />
                Technology & Connectivity
              </label>
              <label>
                <input
                  type="checkbox"
                  name="travelMobility"
                  checked={formData.travelMobility}
                  onChange={handleChange}
                />
                Travel & Mobility
              </label>
              <label>
                <input
                  type="checkbox"
                  name="lodgingAccommodation"
                  checked={formData.lodgingAccommodation}
                  onChange={handleChange}
                />
                Lodging and Accommodation
              </label>
              <label>
                <input
                  type="checkbox"
                  name="industryFacilityTours"
                  checked={formData.industryFacilityTours}
                  onChange={handleChange}
                />
                Industry and Facility Tours
              </label>
              <label>
                <input
                  type="checkbox"
                  name="othersOperation"
                  checked={formData.othersOperation}
                  onChange={handleChange}
                />
                Others
              </label>
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="form-section">
            <h3>Section 4: Business Engagement Areas</h3>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="generalManagement"
                  checked={formData.generalManagement}
                  onChange={handleChange}
                />
                General Management
              </label>
              <label>
                <input
                  type="checkbox"
                  name="operations"
                  checked={formData.operations}
                  onChange={handleChange}
                />
                Operations
              </label>
              <label>
                <input
                  type="checkbox"
                  name="finance"
                  checked={formData.finance}
                  onChange={handleChange}
                />
                Finance
              </label>
              <label>
                <input
                  type="checkbox"
                  name="purchasing"
                  checked={formData.purchasing}
                  onChange={handleChange}
                />
                Purchasing
              </label>
              <label>
                <input
                  type="checkbox"
                  name="marketing"
                  checked={formData.marketing}
                  onChange={handleChange}
                />
                Marketing
              </label>
              <label>
                <input
                  type="checkbox"
                  name="informationTechnology"
                  checked={formData.informationTechnology}
                  onChange={handleChange}
                />
                Information Technology
              </label>
              <label>
                <input
                  type="checkbox"
                  name="humanResources"
                  checked={formData.humanResources}
                  onChange={handleChange}
                />
                Human Resources
              </label>
              <label>
                <input
                  type="checkbox"
                  name="customerService"
                  checked={formData.customerService}
                  onChange={handleChange}
                />
                Customer Service
              </label>
            </div>
            <div className="form-group">
              <label>Other (Specify)</label>
              <input
                type="text"
                name="otherEngagement"
                value={formData.otherEngagement}
                onChange={handleChange}
              />
            </div>
          </div>
        );
      
      case 5:
        return (
          <div className="form-section">
            <h3>Section 5: Value-Added Offerings</h3>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="productServicePromotions"
                  checked={formData.productServicePromotions}
                  onChange={handleChange}
                />
                Product & Service Promotions
              </label>
              <label>
                <input
                  type="checkbox"
                  name="outdoorAdventures"
                  checked={formData.outdoorAdventures}
                  onChange={handleChange}
                />
                Outdoor Adventures (Excursions & Expeditions)
              </label>
              <label>
                <input
                  type="checkbox"
                  name="industryFacilityToursValue"
                  checked={formData.industryFacilityToursValue}
                  onChange={handleChange}
                />
                Industry and Facility Tours
              </label>
              <label>
                <input
                  type="checkbox"
                  name="businessDevelopmentEntrepreneurshipValue"
                  checked={formData.businessDevelopmentEntrepreneurshipValue}
                  onChange={handleChange}
                />
                Business Development & Entrepreneurship
              </label>
              <label>
                <input
                  type="checkbox"
                  name="entertainmentLiveEventsValue"
                  checked={formData.entertainmentLiveEventsValue}
                  onChange={handleChange}
                />
                Entertainment & Live Events
              </label>
              <label>
                <input
                  type="checkbox"
                  name="communityEngagementCSR"
                  checked={formData.communityEngagementCSR}
                  onChange={handleChange}
                />
                Community Engagement & CSR Initiatives
              </label>
              <label>
                <input
                  type="checkbox"
                  name="sportsRecreationValue"
                  checked={formData.sportsRecreationValue}
                  onChange={handleChange}
                />
                Sports & Recreation
              </label>
              <label>
                <input
                  type="checkbox"
                  name="familyRelationshipsCounselling"
                  checked={formData.familyRelationshipsCounselling}
                  onChange={handleChange}
                />
                Family, Relationships & Counselling
              </label>
              <label>
                <input
                  type="checkbox"
                  name="tourismArtsCultureValue"
                  checked={formData.tourismArtsCultureValue}
                  onChange={handleChange}
                />
                Tourism, Arts & Culture
              </label>
              <label>
                <input
                  type="checkbox"
                  name="wellnessHealthRelaxation"
                  checked={formData.wellnessHealthRelaxation}
                  onChange={handleChange}
                />
                Wellness, Health & Relaxation
              </label>
              <label>
                <input
                  type="checkbox"
                  name="travelAccommodation"
                  checked={formData.travelAccommodation}
                  onChange={handleChange}
                />
                Travel & Accommodation
              </label>
              <label>
                <input
                  type="checkbox"
                  name="wealthFinancialPlanning"
                  checked={formData.wealthFinancialPlanning}
                  onChange={handleChange}
                />
                Wealth & Financial Planning
              </label>
            </div>
            <div className="form-group">
              <label>Other</label>
              <input
                type="text"
                name="otherValueAdded"
                value={formData.otherValueAdded}
                onChange={handleChange}
              />
            </div>
          </div>
        );
      
      case 6:
        return (
          <div className="form-section">
            <h3>Section 6: Entity Profile & Engagement</h3>
            <div className="form-group">
              <label>Years of Operation</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="yearsOfOperation"
                    value="<1"
                    checked={formData.yearsOfOperation === '<1'}
                    onChange={handleChange}
                  />
                  &lt;1
                </label>
                <label>
                  <input
                    type="radio"
                    name="yearsOfOperation"
                    value="1–5"
                    checked={formData.yearsOfOperation === '1–5'}
                    onChange={handleChange}
                  />
                  1–5
                </label>
                <label>
                  <input
                    type="radio"
                    name="yearsOfOperation"
                    value="6–10"
                    checked={formData.yearsOfOperation === '6–10'}
                    onChange={handleChange}
                  />
                  6–10
                </label>
                <label>
                  <input
                    type="radio"
                    name="yearsOfOperation"
                    value=">10"
                    checked={formData.yearsOfOperation === '>10'}
                    onChange={handleChange}
                  />
                  10
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <label>Target Market</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="targetMarket"
                    value="District"
                    checked={formData.targetMarket === 'District'}
                    onChange={handleChange}
                  />
                  District
                </label>
                <label>
                  <input
                    type="radio"
                    name="targetMarket"
                    value="National"
                    checked={formData.targetMarket === 'National'}
                    onChange={handleChange}
                  />
                  National
                </label>
                <label>
                  <input
                    type="radio"
                    name="targetMarket"
                    value="International"
                    checked={formData.targetMarket === 'International'}
                    onChange={handleChange}
                  />
                  International
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <label>Age Demographics</label>
              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="ageDemographics"
                    value="<12"
                    checked={formData.ageDemographics.includes('<12')}
                    onChange={handleCheckboxArray}
                  />
                  &lt;12
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="ageDemographics"
                    value="13–20"
                    checked={formData.ageDemographics.includes('13–20')}
                    onChange={handleCheckboxArray}
                  />
                  13–20
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="ageDemographics"
                    value="21–35"
                    checked={formData.ageDemographics.includes('21–35')}
                    onChange={handleCheckboxArray}
                  />
                  21–35
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="ageDemographics"
                    value="36–49"
                    checked={formData.ageDemographics.includes('36–49')}
                    onChange={handleCheckboxArray}
                  />
                  36–49
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="ageDemographics"
                    value="50–64"
                    checked={formData.ageDemographics.includes('50–64')}
                    onChange={handleCheckboxArray}
                  />
                  50–64
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="ageDemographics"
                    value="65+"
                    checked={formData.ageDemographics.includes('65+')}
                    onChange={handleCheckboxArray}
                  />
                  65+
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <label>Effective Marketing Channels</label>
              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="effectiveMarketingChannels"
                    value="Social media"
                    checked={formData.effectiveMarketingChannels.includes('Social media')}
                    onChange={handleCheckboxArray}
                  />
                  Social media
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="effectiveMarketingChannels"
                    value="Events"
                    checked={formData.effectiveMarketingChannels.includes('Events')}
                    onChange={handleCheckboxArray}
                  />
                  Events
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="effectiveMarketingChannels"
                    value="Traditional Media"
                    checked={formData.effectiveMarketingChannels.includes('Traditional Media')}
                    onChange={handleCheckboxArray}
                  />
                  Traditional Media
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="effectiveMarketingChannels"
                    value="Promotional Materials"
                    checked={formData.effectiveMarketingChannels.includes('Promotional Materials')}
                    onChange={handleCheckboxArray}
                  />
                  Promotional Materials
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="effectiveMarketingChannels"
                    value="Other"
                    checked={formData.effectiveMarketingChannels.includes('Other')}
                    onChange={handleCheckboxArray}
                  />
                  Other
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <label>Preferred collaboration frequency with FMLC</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="collaborationFrequency"
                    value="Weekly"
                    checked={formData.collaborationFrequency === 'Weekly'}
                    onChange={handleChange}
                  />
                  Weekly
                </label>
                <label>
                  <input
                    type="radio"
                    name="collaborationFrequency"
                    value="Monthly/Quarterly"
                    checked={formData.collaborationFrequency === 'Monthly/Quarterly'}
                    onChange={handleChange}
                  />
                  Monthly/Quarterly
                </label>
                <label>
                  <input
                    type="radio"
                    name="collaborationFrequency"
                    value="Occasionally (Special Events Only)"
                    checked={formData.collaborationFrequency === 'Occasionally (Special Events Only)'}
                    onChange={handleChange}
                  />
                  Occasionally (Special Events Only)
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <label>Would you co-host, sponsor, or mentor within the Club network?</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="coHostSponsorMentor"
                    value="Yes"
                    checked={formData.coHostSponsorMentor === 'Yes'}
                    onChange={handleChange}
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="coHostSponsorMentor"
                    value="No"
                    checked={formData.coHostSponsorMentor === 'No'}
                    onChange={handleChange}
                  />
                  No
                </label>
              </div>
            </div>
            {formData.coHostSponsorMentor === 'Yes' && (
              <div className="checkbox-group">
                <label>Specify how:</label>
                <label>
                  <input
                    type="checkbox"
                    name="specifyHow"
                    value="Financial"
                    checked={formData.specifyHow.includes('Financial')}
                    onChange={handleCheckboxArray}
                  />
                  Financial
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="specifyHow"
                    value="Products"
                    checked={formData.specifyHow.includes('Products')}
                    onChange={handleCheckboxArray}
                  />
                  Products
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="specifyHow"
                    value="Services"
                    checked={formData.specifyHow.includes('Services')}
                    onChange={handleCheckboxArray}
                  />
                  Services
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="specifyHow"
                    value="Other"
                    checked={formData.specifyHow.includes('Other')}
                    onChange={handleCheckboxArray}
                  />
                  Other
                </label>
              </div>
            )}
          </div>
        );
      
      case 7:
        return (
          <div className="form-section">
            <h3>Section 7: Supplier Empowerment & Business Support</h3>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="clientCustomerSourcing"
                  checked={formData.clientCustomerSourcing}
                  onChange={handleChange}
                />
                Client, customer sourcing
              </label>
              <label>
                <input
                  type="checkbox"
                  name="marketOpportunitiesSupport"
                  checked={formData.marketOpportunitiesSupport}
                  onChange={handleChange}
                />
                Market opportunities and support
              </label>
              <label>
                <input
                  type="checkbox"
                  name="betterPricesDealSuppliers"
                  checked={formData.betterPricesDealSuppliers}
                  onChange={handleChange}
                />
                Better prices deal from suppliers
              </label>
              <label>
                <input
                  type="checkbox"
                  name="accessPreferredVendors"
                  checked={formData.accessPreferredVendors}
                  onChange={handleChange}
                />
                Access to preferred vendors
              </label>
              <label>
                <input
                  type="checkbox"
                  name="strategicSourcingSupport"
                  checked={formData.strategicSourcingSupport}
                  onChange={handleChange}
                />
                Strategic sourcing support
              </label>
              <label>
                <input
                  type="checkbox"
                  name="representationSupplierNegotiations"
                  checked={formData.representationSupplierNegotiations}
                  onChange={handleChange}
                />
                Representation during supplier negotiations
              </label>
              <label>
                <input
                  type="checkbox"
                  name="flexibleSupplierPaymentTerms"
                  checked={formData.flexibleSupplierPaymentTerms}
                  onChange={handleChange}
                />
                Flexible supplier payment terms
              </label>
              <label>
                <input
                  type="checkbox"
                  name="jointSourcingEfforts"
                  checked={formData.jointSourcingEfforts}
                  onChange={handleChange}
                />
                Joint sourcing efforts with FMLC partners
              </label>
              <label>
                <input
                  type="checkbox"
                  name="businessDevelopmentServices"
                  checked={formData.businessDevelopmentServices}
                  onChange={handleChange}
                />
                Business development services
              </label>
              <label>
                <input
                  type="checkbox"
                  name="enterpriseSupportServices"
                  checked={formData.enterpriseSupportServices}
                  onChange={handleChange}
                />
                Enterprise support services provision
              </label>
            </div>
            <div className="form-group">
              <label>Other</label>
              <input
                type="text"
                name="otherSupplierEmpowerment"
                value={formData.otherSupplierEmpowerment}
                onChange={handleChange}
              />
            </div>
          </div>
        );
      
      case 8:
        return (
          <div className="form-section">
            <h3>Section 8: Administration Fee & Payment Details</h3>
            <p>Monthly Administration Fee: M500.00</p>
            <p>Annual Administration Fee: M6,000.00</p>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="agreeAdminFee"
                  checked={formData.agreeAdminFee}
                  onChange={handleChange}
                />
                I agree to pay the monthly FMLC administration fee. Yes
              </label>
            </div>
            
            <div className="form-group">
              <label>Payment Method (tick one)</label>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="Cash"
                    checked={formData.paymentMethod === 'Cash'}
                    onChange={handleChange}
                  />
                  Cash
                </label>
                <label>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="Mobile Money Provider (M-Pesa, EcoCash)"
                    checked={formData.paymentMethod === 'Mobile Money Provider (M-Pesa, EcoCash)'}
                    onChange={handleChange}
                  />
                  Mobile Money Provider (M-Pesa, EcoCash)
                </label>
                <label>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="Bank Deposit/EFT"
                    checked={formData.paymentMethod === 'Bank Deposit/EFT'}
                    onChange={handleChange}
                  />
                  Bank Deposit/EFT
                </label>
              </div>
            </div>
            
            <div className="form-group">
              <label>M-Pesa Number</label>
              <input
                type="text"
                name="mPesaNumber"
                value={formData.mPesaNumber}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label>EcoCash Number</label>
              <input
                type="text"
                name="ecoCashNumber"
                value={formData.ecoCashNumber}
                onChange={handleChange}
              />
            </div>
            
            <h4>Bank Details for Debit Order</h4>
            <div className="form-group">
              <label>Account Holder</label>
              <input
                type="text"
                name="accountHolder"
                value={formData.accountHolder}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Bank Name</label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Account No.</label>
              <input
                type="text"
                name="accountNo"
                value={formData.accountNo}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Branch Code</label>
              <input
                type="text"
                name="branchCode"
                value={formData.branchCode}
                onChange={handleChange}
              />
            </div>
          </div>
        );
      
      case 9:
        return (
          <div className="form-section">
            <h3>Section 9: Agreement & Signature</h3>
            <div className="form-group">
              <label>Authorized Signature</label>
              <input
                type="text"
                name="authorizedSignature"
                value={formData.authorizedSignature}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
              />
            </div>
            
            <h3>Attachments</h3>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="personalIdChecked"
                  checked={formData.personalIdChecked}
                  onChange={handleChange}
                />
                Personal ID - FMLC Liaison
              </label>
              {formData.personalIdChecked && (
                <input
                  type="file"
                  name="personalIdFile"
                  onChange={handleChange}
                  accept="image/*,.pdf"
                />
              )}
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="businessIdChecked"
                  checked={formData.businessIdChecked}
                  onChange={handleChange}
                />
                Business ID
              </label>
              {formData.businessIdChecked && (
                <input
                  type="file"
                  name="businessIdFile"
                  onChange={handleChange}
                  accept="image/*,.pdf"
                />
              )}
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="businessTradersLicenseChecked"
                  checked={formData.businessTradersLicenseChecked}
                  onChange={handleChange}
                />
                Business Trader’s License
              </label>
              {formData.businessTradersLicenseChecked && (
                <input
                  type="file"
                  name="businessTradersLicenseFile"
                  onChange={handleChange}
                  accept="image/*,.pdf"
                />
              )}
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="taxClearanceCertificateChecked"
                  checked={formData.taxClearanceCertificateChecked}
                  onChange={handleChange}
                />
                Tax Clearance Certificate
              </label>
              {formData.taxClearanceCertificateChecked && (
                <input
                  type="file"
                  name="taxClearanceCertificateFile"
                  onChange={handleChange}
                  accept="image/*,.pdf"
                />
              )}
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="businessUtilityBillChecked"
                  checked={formData.businessUtilityBillChecked}
                  onChange={handleChange}
                />
                Business Utility Bill
              </label>
              {formData.businessUtilityBillChecked && (
                <input
                  type="file"
                  name="businessUtilityBillFile"
                  onChange={handleChange}
                  accept="image/*,.pdf"
                />
              )}
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="businessSubleaseAgreementChecked"
                  checked={formData.businessSubleaseAgreementChecked}
                  onChange={handleChange}
                />
                Business Sublease Agreement
              </label>
              {formData.businessSubleaseAgreementChecked && (
                <input
                  type="file"
                  name="businessSubleaseAgreementFile"
                  onChange={handleChange}
                  accept="image/*,.pdf"
                />
              )}
            </div>
          </div>
        );
      
      case 10:
        return (
          <div className="form-section">
            <h3>Section 10: Business Partner Application Declaration</h3>
            <p>[Declaration text from PDF]</p>
            
            <h4>1. BUSINESS PARTNER (APPLICANT)</h4>
            <div className="form-group">
              <label>Title & Full Names</label>
              <input
                type="text"
                name="applicantTitleFullNames"
                value={formData.applicantTitleFullNames}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Position</label>
              <input
                type="text"
                name="applicantPosition"
                value={formData.applicantPosition}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Business Name</label>
              <input
                type="text"
                name="applicantBusinessName"
                value={formData.applicantBusinessName}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                name="applicantDate"
                value={formData.applicantDate}
                onChange={handleChange}
              />
            </div>
            
            <h4>2. FMLC REPRESENTATIVE</h4>
            <div className="form-group">
              <label>Title & Full Names</label>
              <input
                type="text"
                name="representativeTitleFullNames"
                value={formData.representativeTitleFullNames}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Position</label>
              <input
                type="text"
                name="representativePosition"
                value={formData.representativePosition}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                name="representativeDate"
                value={formData.representativeDate}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Signature</label>
              <input
                type="text"
                name="representativeSignature"
                value={formData.representativeSignature}
                onChange={handleChange}
              />
            </div>
            
            <h4>3. FMLC APPROVER</h4>
            <div className="form-group">
              <label>Title & Full Names</label>
              <input
                type="text"
                name="approverTitleFullNames"
                value={formData.approverTitleFullNames}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Position</label>
              <input
                type="text"
                name="approverPosition"
                value={formData.approverPosition}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                name="approverDate"
                value={formData.approverDate}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Signature</label>
              <input
                type="text"
                name="approverSignature"
                value={formData.approverSignature}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label>Other Comments</label>
              <textarea
                name="otherComments"
                value={formData.otherComments}
                onChange={handleChange}
                rows="3"
              />
            </div>
            
            <p>Please review all your information before submitting.</p>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        );
      
      default:
        return <div>Invalid step</div>;
    }
  };

  return (
    <div className="form-container">
      <h2>FINITE MAGAZINE LIFESTYLE CLUB BUSINESS PARTNER APPLICATION</h2>
      
      <div className="progress-bar">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(step => (
          <div
            key={step}
            className={`progress-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
          >
            {step}
          </div>
        ))}
      </div>
      
      <form onSubmit={handleSubmit}>
        {renderStep()}
        
        <div className="form-navigation">
          {currentStep > 1 && (
            <button type="button" onClick={prevStep} className="btn-secondary">
              Previous
            </button>
          )}
          
          {currentStep < 10 ? (
            <button type="button" onClick={nextStep} className="btn-primary">
              {saving ? 'Saving...' : 'Next'}
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default PartnerForm;